// The process map: every step a box, every "this happened next" an arrow, top to bottom from Start to End.
// Arrow thickness is how often (or, in the time view, how much of the total time sits there). Arrows going back to an
// earlier step are dashed and routed down the right-hand side. Color only marks what the miner flagged.
import { useMemo } from "react";
import { durShort, type PEdge, type PNode } from "./pmApi";

export type Sel = { type: "edge"; from: string; to: string } | { type: "node"; id: string } | null;
export type Mode = "count" | "time";
export type Detail = "main" | "more" | "all";

const W = 184, NH = 48, GX = 28, GY = 76, PAD = 24, TERM = 30;
const START = "__start", END = "__end";

type Placed = { id: string; x: number; y: number; layer: number; node?: PNode };

export function ProcessMap({ nodes, edges, starts, ends, cases, mode, detail, flags, sel, onSelect }: {
  nodes: PNode[]; edges: PEdge[]; starts: { to: string; count: number }[]; ends: { from: string; count: number }[]; cases: number;
  mode: Mode; detail: Detail; flags: Map<string, "hot" | "loop" | "auto">; sel: Sel; onSelect: (s: Sel) => void;
}) {
  const g = useMemo(() => {
    const min = detail === "main" ? 0.1 : detail === "more" ? 0.03 : 0;
    const keepNode = (n: PNode) => n.id === "Created" || n.cases >= min * cases || [...flags.keys()].some((k) => k.split("→").includes(n.id));
    const vis = nodes.filter(keepNode);
    const order = new Map(vis.map((n, i) => [n.id, i]));
    const flagged = (e: PEdge) => flags.has(`${e.from}→${e.to}`);
    const es = edges.filter((e) => order.has(e.from) && order.has(e.to) && (e.count >= min * cases || flagged(e)));
    const forward = es.filter((e) => order.get(e.from)! < order.get(e.to)!);
    const back = es.filter((e) => order.get(e.from)! >= order.get(e.to)!);

    // Layers: the longest chain of forward steps to reach a node.
    const layer = new Map<string, number>();
    for (const n of vis) {
      const preds = forward.filter((e) => e.to === n.id).map((e) => (layer.get(e.from) ?? 0) + 1);
      layer.set(n.id, preds.length ? Math.max(...preds) : 0);
    }
    const nLayers = Math.max(0, ...layer.values()) + 1;
    const rows: string[][] = Array.from({ length: nLayers }, () => []);
    for (const n of vis) rows[layer.get(n.id)!].push(n.id);
    // Order within a layer by where the steps feeding them sit (fewer crossings), first layer by how often.
    const xIndex = new Map<string, number>();
    rows.forEach((row, li) => {
      if (li > 0) row.sort((a, b) => bary(a) - bary(b));
      row.forEach((id, i) => xIndex.set(id, i - (row.length - 1) / 2));
      function bary(id: string) { const p = forward.filter((e) => e.to === id && xIndex.has(e.from)); return p.length ? p.reduce((s, e) => s + xIndex.get(e.from)! * e.count, 0) / p.reduce((s, e) => s + e.count, 0) : 0; }
    });
    const widest = Math.max(1, ...rows.map((r) => r.length));
    const inner = widest * W + (widest - 1) * GX;
    const backLanes = back.length ? 60 + back.length * 16 : 0;
    const width = PAD * 2 + inner + backLanes;
    const placed = new Map<string, Placed>();
    const top = PAD + TERM + GY * 0.6;
    for (const n of vis) {
      const li = layer.get(n.id)!;
      placed.set(n.id, { id: n.id, layer: li, node: n, x: PAD + inner / 2 + xIndex.get(n.id)! * (W + GX) - W / 2, y: top + li * (NH + GY) });
    }
    const cx = PAD + inner / 2;
    placed.set(START, { id: START, layer: -1, x: cx - 34, y: PAD });
    const endY = top + nLayers * (NH + GY) - GY * 0.4;
    placed.set(END, { id: END, layer: nLayers, x: cx - 34, y: endY });
    const height = endY + TERM + PAD;
    // Ports: arrows leave the bottom and enter the top of a box spread out, ordered left to right, so they don't pile onto one point.
    const startEdges = starts.filter((s) => placed.has(s.to)), endEdges = ends.filter((s) => placed.has(s.from));
    const outs = new Map<string, string[]>(), ins = new Map<string, string[]>();
    const cxOf = (id: string) => placed.get(id)!.x + (id === START || id === END ? 34 : W / 2);
    const add = (m: Map<string, string[]>, id: string, other: string) => { let l = m.get(id); if (!l) m.set(id, (l = [])); l.push(other); };
    for (const e of forward) { add(outs, e.from, e.to); add(ins, e.to, e.from); }
    for (const s of startEdges) add(ins, s.to, START);
    for (const s of endEdges) add(outs, s.from, END);
    const port = (m: Map<string, string[]>, id: string, other: string) => {
      const l = [...(m.get(id) ?? [])].sort((a, b) => cxOf(a) - cxOf(b));
      const i = l.indexOf(other), n = l.length;
      return n <= 1 || id === START || id === END ? (id === START || id === END ? 34 : W / 2) : W * (0.22 + (0.56 * i) / (n - 1));
    };
    return { vis, es, forward, back, placed, width, height, inner, startEdges, endEdges, outPort: (from: string, to: string) => port(outs, from, to), inPort: (to: string, from: string) => port(ins, to, from) };
  }, [nodes, edges, starts, ends, cases, detail, flags]);

  const maxW = Math.max(1, ...g.es.map((e) => (mode === "count" ? e.count : e.totalMs)));
  const stroke = (e: PEdge) => 1.2 + 6 * Math.sqrt((mode === "count" ? e.count : e.totalMs) / maxW);
  const maxS = Math.max(1, ...g.startEdges.map((s) => s.count), ...g.endEdges.map((s) => s.count));

  const curve = (a: Placed, b: Placed, _aw: number, _bw: number) => {
    const x1 = a.x + g.outPort(a.id, b.id), y1 = a.y + (a.id === START ? TERM : NH), x2 = b.x + g.inPort(b.id, a.id), y2 = b.y, dy = Math.max(30, (y2 - y1) * 0.5);
    return { d: `M${x1},${y1} C${x1},${y1 + dy} ${x2},${y2 - dy} ${x2},${y2 - 6}`, mx: (x1 + 3 * x1 + 3 * x2 + x2) / 8, my: (y1 + 3 * (y1 + dy) + 3 * (y2 - dy) + y2) / 8 };
  };

  return (
    <svg width={g.width} height={g.height} viewBox={`0 0 ${g.width} ${g.height}`} role="img" aria-label="Process map" style={{ maxWidth: "none" }}>
      <defs>
        {["g", "hot", "loop", "auto", "sel"].map((k) => (
          <marker key={k} id={`pm-ar-${k}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="9" markerHeight="9" markerUnits="userSpaceOnUse" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" className={`pm-mk ${k}`} />
          </marker>
        ))}
      </defs>

      {/* Start and end */}
      {g.startEdges.map((s) => { const c = curve(g.placed.get(START)!, g.placed.get(s.to)!, 68, W); return (
        <g key={`s-${s.to}`} className="edge"><path className="l" d={c.d} strokeWidth={1 + 3 * (s.count / maxS)} markerEnd="url(#pm-ar-g)" /></g>); })}
      {g.endEdges.map((s) => { const c = curve(g.placed.get(s.from)!, g.placed.get(END)!, W, 68); return (
        <g key={`e-${s.from}`} className="edge"><path className="l" d={c.d} strokeWidth={1 + 3 * (s.count / maxS)} markerEnd="url(#pm-ar-g)" /></g>); })}

      {/* Forward steps */}
      {g.forward.map((e) => {
        const a = g.placed.get(e.from)!, b = g.placed.get(e.to)!;
        const c = curve(a, b, W, W);
        const f = flags.get(`${e.from}→${e.to}`);
        const isSel = sel?.type === "edge" && sel.from === e.from && sel.to === e.to;
        const label = mode === "count" ? String(e.count) : durShort(e.medianMs);
        return (
          <g key={`${e.from}→${e.to}`} className={`edge ${f ?? ""} ${isSel ? "sel" : ""}`} onClick={() => onSelect(isSel ? null : { type: "edge", from: e.from, to: e.to })}>
            <title>{`${e.from} → ${e.to}: ${e.count} times, median ${durShort(e.medianMs)}`}</title>
            <path className="hit" d={c.d} />
            <path className="l" d={c.d} strokeWidth={stroke(e)} markerEnd={`url(#pm-ar-${isSel ? "sel" : f ?? "g"})`} />
            <rect className="lb" x={c.mx - label.length * 3.6 - 5} y={c.my - 9} width={label.length * 7.2 + 10} height={17} rx={3} />
            <text x={c.mx} y={c.my + 4} textAnchor="middle">{label}</text>
          </g>
        );
      })}

      {/* Going back: out of the bottom of the step, up the right-hand side through the gaps between rows, into the top of the earlier step */}
      {g.back.map((e, k) => {
        const a = g.placed.get(e.from)!, b = g.placed.get(e.to)!;
        const f = flags.get(`${e.from}→${e.to}`);
        const isSel = sel?.type === "edge" && sel.from === e.from && sel.to === e.to;
        const label = mode === "count" ? String(e.count) : durShort(e.medianMs);
        let d: string, mx: number, my: number;
        if (a.id === b.id) {
          const x = a.x + W, y = a.y + NH / 2;
          d = `M${x},${y + 8} C${x + 46},${y + 26} ${x + 46},${y - 26} ${x + 6},${y - 8}`; mx = x + 38; my = y;
        } else {
          const lane = PAD + g.inner + 36 + k * 16, r = 8;
          const sx = a.x + W * (0.78 - k * 0.04), sy = a.y + NH, gapBelow = sy + 14 + k * 4;
          const tx = b.x + W * (0.78 - k * 0.04), ty = b.y, gapAbove = ty - 14 - k * 4;
          d = `M${sx},${sy} V${gapBelow - r} Q${sx},${gapBelow} ${sx + r},${gapBelow} H${lane - r} Q${lane},${gapBelow} ${lane},${gapBelow - r} V${gapAbove + r} Q${lane},${gapAbove} ${lane - r},${gapAbove} H${tx + r} Q${tx},${gapAbove} ${tx},${gapAbove + r} V${ty - 1}`;
          mx = lane; my = (gapBelow + gapAbove) / 2;
        }
        return (
          <g key={`${e.from}→${e.to}`} className={`edge back ${f ?? ""} ${isSel ? "sel" : ""}`} onClick={() => onSelect(isSel ? null : { type: "edge", from: e.from, to: e.to })}>
            <title>{`Back from ${e.from} to ${e.to}: ${e.count} times`}</title>
            <path className="hit" d={d} />
            <path className="l" d={d} strokeWidth={Math.min(4, stroke(e))} markerEnd={`url(#pm-ar-${isSel ? "sel" : f ?? "g"})`} />
            <rect className="lb" x={mx - label.length * 3.6 - 5} y={my - 9} width={label.length * 7.2 + 10} height={17} rx={3} />
            <text x={mx} y={my + 4} textAnchor="middle">{label}</text>
          </g>
        );
      })}

      {/* Steps */}
      {g.vis.map((n) => {
        const p = g.placed.get(n.id)!;
        const isSel = sel?.type === "node" && sel.id === n.id;
        const name = n.id.length > 24 ? `${n.id.slice(0, 23)}…` : n.id;
        return (
          <g key={n.id} className={`node ${isSel ? "sel" : ""}`} transform={`translate(${p.x},${p.y})`} onClick={() => onSelect(isSel ? null : { type: "node", id: n.id })} style={{ cursor: "pointer" }}>
            <title>{`${n.id}: ${n.cases} cases${n.repeats ? `, repeated ${n.repeats} times` : ""}`}</title>
            <rect width={W} height={NH} rx={8} />
            <text className="t" x={12} y={20}>{name}</text>
            <text className="s" x={12} y={37}>{n.cases.toLocaleString()} {n.cases === 1 ? "case" : "cases"}</text>
            {n.repeats > 0 && <text className="r" x={W - 10} y={37} textAnchor="end">↺ {n.repeats}</text>}
          </g>
        );
      })}
      {[START, END].map((id) => { const p = g.placed.get(id)!; return (
        <g key={id} className="term" transform={`translate(${p.x},${p.y})`}><rect width={68} height={TERM} rx={15} /><text x={34} y={19} textAnchor="middle">{id === START ? "Start" : "End"}</text></g>); })}
    </svg>
  );
}
